import { TrashIcon } from "@xola/icons";
import { Loader, SecondaryButton } from "common";
import React from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { Experience, PartnerPackage, XolaPartner } from "types";
import { api } from "@/api/api";
import { InlineConfirmDeletePopover } from "@/components/Common/InlineConfirmDeletePopover";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ProductImage } from "@/components/Common/ProductImage";
import { PromptModal } from "@/components/Common/PromptModal";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";

interface DisconnectPartnerModalProps {
    isVisible: boolean;
    data: XolaPartner;
    onConfirm: (e: any) => void;
    onClose: (e: any) => void;
}
export const DisconnectPartnerModal = ({ isVisible, data, onConfirm, onClose }: DisconnectPartnerModalProps) => {
    const { data: partnerExperiences, isLoading: isLoadingPartnerExperiences } = useAutoFetchCollection<Experience>(
        "experiences",
        {
            partnerExperiences: true,
            partnerSeller: data.partnerSeller.id,
        },
    );

    const partnerExperienceIds = partnerExperiences?.map((experience) => experience.id) ?? [];

    const { data: partnerPackages, isLoading: isLoadingPartnerPackages } = useAutoFetchCollection<PartnerPackage>(
        "partnerPackages",
        {
            ...(partnerExperienceIds.length > 0 && { "selectedProducts[in]": partnerExperienceIds.join(",") }),
        },
        { enabled: !isLoadingPartnerExperiences },
    );

    const isLoading = isLoadingPartnerExperiences && isLoadingPartnerPackages;

    return (
        <PromptModal
            isOpen={isVisible}
            title="Remove Operator"
            dialogText={{ secondary: "Cancel", submit: "Remove" }}
            dialogDataTestIds={{ secondaryTestId: "cancel-button", submitTestId: "remove-button" }}
            isLoading={isLoading}
            isPrimaryButtonDisabled={partnerPackages.length > 0}
            onConfirm={onConfirm}
            onClose={onClose}
        >
            {partnerPackages.length > 0 ? (
                <>
                    <div>
                        You currently have the following packages configured with{" "}
                        <b>
                            <var data-var="name"> {data?.name}</var>
                        </b>{" "}
                        products:
                    </div>
                    <PartnerPackageList partnerPackages={partnerPackages} isLoading={isLoading} />
                    <div className="mt-5">
                        Before removing this operator, you must either remove this operator&apos;s products from your
                        packages and buttons, or delete them.
                    </div>
                </>
            ) : (
                <>
                    <div>
                        Are you sure you want to remove{" "}
                        <b>
                            <var data-var="name"> {data?.name}</var>
                        </b>{" "}
                        as an operator?
                    </div>
                    <div className="mt-5">
                        You will no longer be able to distribute their products, including any packages that have been
                        configured containing this operator&apos;s products.
                    </div>
                </>
            )}
        </PromptModal>
    );
};

const PartnerPackageList = ({
    partnerPackages,
    isLoading,
}: {
    partnerPackages: PartnerPackage[];
    isLoading: boolean;
}) => {
    if (isLoading) {
        return <Loader size="tiny" className="p-2" text="Loading Partner Packages" />;
    }

    return (
        <div className="mt-5 h-50 w-full flex-1 flex-col overflow-y-scroll">
            {partnerPackages.map((pkg) => (
                <PartnerPackageTile key={pkg.id} pkg={pkg} />
            ))}
        </div>
    );
};

const PartnerPackageTile = ({ pkg }: { pkg: PartnerPackage }) => {
    const history = useHistory();
    const invalidate = useInvalidate();
    const { mutateAsync: deletePackage } = useMutation(async (id: ID) => {
        return api.delete(`/partnerPackages/${id}`);
    });

    const handleDelete = async (e: any) => {
        e.stopPropagation();
        await deletePackage(pkg.id);
        invalidate("partnerPackages", pkg.id);
    };

    const handleClick = () => {
        history.push(`/products/partnerPackages/${pkg.id}/description`);
    };

    return (
        <div className="border-b border-b-gray-light bg-white p-2">
            <div className="flex cursor-pointer items-center justify-between" onClick={handleClick}>
                <div className="flex items-center">
                    <ProductImage product={pkg} size="small" />
                    <div className="space-y-2">
                        <NoTranslate as="div" className="font-semibold">
                            {pkg.name}
                        </NoTranslate>
                    </div>
                </div>
                <InlineConfirmDeletePopover confirmButtonText="Confirm" onConfirm={handleDelete}>
                    <SecondaryButton size="small">
                        <TrashIcon />
                    </SecondaryButton>
                </InlineConfirmDeletePopover>
            </div>
        </div>
    );
};
